import React from 'react';

export enum UserRole {
  ADMIN = 'ADMIN',
  SUPER_ADMIN = 'SUPER_ADMIN',
  MANAGER = 'MANAGER',
  ACCOUNTANT = 'ACCOUNTANT',
  CUSTOMER_ADMIN = 'CUSTOMER_ADMIN',
  CUSTOMER_BRANCH_ADMIN = 'CUSTOMER_BRANCH_ADMIN',
  CUSTOMER_MANAGER = 'CUSTOMER_MANAGER',
  CUSTOMER = 'CUSTOMER',
}

export interface User {
  id: number | string;
  name: string;
  email: string;
  password?: string;
  role: UserRole;
  phone?: string;
  billingAddress?: string;
  shippingAddress?: string;
  gstNumber?: string;
  isApproved?: boolean;
  organizationAdminId?: number | string | null;
  branchId?: number | null;
  organizationName?: string;
}

export interface ProductVariation {
  id: string;
  name: string;
  price: number;
  stock: number;
}

export interface Category {
  id: number;
  name: string;
  parentId: number | null;
  children?: Category[];
}

export interface Product {
  id: number;
  name: string;
  description: string;
  imageUrl: string;
  hsnCode?: string;
  stockQuantity: number;
  basePrice?: number;
  categoryId?: number;
  subCategoryId?: number;
  variations?: ProductVariation[];
  customerPrices?: Record<string, number>;
  customerVariationPrices?: Record<string, Record<string, number>>;
  branchPrices?: Record<string, number>;
  branchVariationPrices?: Record<string, Record<string, number>>;
}

export interface OrderItem {
  id?: number;
  productId: number;
  productName: string;
  variationId?: string;
  variationName?: string;
  quantity: number;
  pricePerUnit?: number;
  packed?: boolean;
  receivedQuantity?: number;
  receiveRemark?: string;
}

export enum OrderStatus {
  PENDING_CUSTOMER_APPROVAL = 'PENDING_CUSTOMER_APPROVAL',
  CUSTOMER_DENIED = 'CUSTOMER_DENIED',
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  APPROVED = 'APPROVED',
  SHIPPED = 'SHIPPED',
  COMPLETED = 'COMPLETED',
  ISSUE_REPORTED = 'ISSUE_REPORTED',
  PAID = 'PAID',
}

export interface Order {
  id: number;
  customerId: number | string;
  customerName: string;
  branchId?: number | null;
  branchName?: string;
  billingAddress?: string;
  shippingAddress?: string;
  gstNumber?: string;
  items: OrderItem[];
  status: OrderStatus;
  createdAt: Date;
  totalAmount: number;
  customerAdminId?: number | string | null;
  submittedById?: number | string | null;
  approvedById?: number | string | null;
  approvedAt?: Date;
  deniedById?: number | string | null;
  deniedAt?: Date;
  approvalRemark?: string;
  shipmentId?: number | null;
  trackingNumber?: string;
  courierPartner?: string;
  shippedAt?: Date;
  deliveredAt?: Date;
}

export type InvoiceStatus = 'Unpaid' | 'Paid' | 'Void';

export interface Invoice {
  id: number;
  orderId: number;
  customerId: number | string;
  customerName: string;
  amount: number;
  taxRate?: number;
  notes?: string;
  issuedAt: Date;
  dueDate: Date;
  status: InvoiceStatus;
}

export interface Testimonial {
  id: number;
  customerId: number | string;
  customerName: string;
  productId: number;
  productName: string;
  rating: number;
  comment: string;
  createdAt: Date;
}

export interface Inquiry {
  id: number;
  name: string;
  email: string;
  company: string;
  message: string;
  status: 'New' | 'Read' | 'Replied';
  createdAt: Date;
}

export type NotificationType = 'success' | 'error' | 'info';
export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export interface Notification {
  id: number;
  userId?: number | string;
  message: string;
  type: NotificationType;
  count?: number;
  isRead?: boolean;
  title?: string;
  externalKey?: string;
  entityType?: string;
  entityId?: number;
  metadata?: Record<string, any> | null;
  createdAt: Date;
}

export interface SiteContent {
  key: string;
  value: any;
  section: string;
}

export interface ThemeConfig {
  primaryColor: string;
  borderRadius: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  fontFamily: string;
}

export interface LogoConfig {
  type: 'text' | 'image';
  text: string;
  imageUrl?: string;
  height: number;
  footerHeight?: number;
}

export interface AnnouncementBar {
  enabled: boolean;
  text: string;
  backgroundColor: string;
  textColor: string;
}

export interface SocialLinks {
  facebook?: string;
  twitter?: string;
  linkedin?: string;
  instagram?: string;
}

export interface FeatureBlock {
  title: string;
  description: string;
  iconName: 'stock' | 'quality' | 'savings' | 'delivery' | 'support';
}

export interface FooterLink {
  label: string;
  page: string;
}

export interface FooterColumn {
  title: string;
  links: FooterLink[];
}

export interface InvoiceConfig {
  companyName: string;
  address: string;
  gstin: string;
  msmeNumber: string;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
  accountName: string;
  terms: string[];
  signatoryLabel: string;
  signatureUrl?: string;
  stampUrl?: string;
  placeOfSupply?: string;
  defaultPaymentTerms?: string;
  documentTitle?: string;
  accentColor?: string;
  showCompanyMark?: boolean;
  showStamp?: boolean;
  showSignature?: boolean;
  footerNote?: string;
}

export interface CMSData {
  home_hero: {
    title: string;
    subtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
  };
  footer_info: {
    companyName: string;
    description: string;
    copyrightText: string;
    showSocials: boolean;
  };
  footer_columns?: FooterColumn[];
  contact_info: {
    title: string;
    subtitle: string;
    address: string;
    phone: string;
    email: string;
    officeHours?: string;
    inquiryOptions?: string[];
    contactSectionTitle?: string;
    formTitle?: string;
    successTitle?: string;
    successMessage?: string;
  };
  theme_config?: ThemeConfig;
  logo_config?: LogoConfig;
  announcement_bar?: AnnouncementBar;
  social_links?: SocialLinks;
  features_section?: FeatureBlock[];
  invoice_config?: InvoiceConfig;
  website_pages?: Record<string, any>;
}

export interface InventoryItem {
  id: string | number;
  branchId?: number | null;
  branchName?: string;
  productId: number;
  productName: string;
  variationId?: string;
  variationName?: string;
  quantity: number;
  lowStockThreshold: number;
  organizationAdminId?: string | number;
}

export interface InventoryUsageLog {
  id: number;
  inventoryItemId: number | string;
  organizationAdminId: string | number;
  branchId?: number | null;
  productId: number;
  productName: string;
  variationId?: string;
  variationName?: string;
  quantity: number;
  note?: string;
  createdBy?: string | number | null;
  createdByName?: string;
  createdAt: Date;
}

export interface ReorderRule {
  id: number;
  organizationAdminId: string | number;
  branchId?: number | null;
  productId: number;
  variationId?: string;
  threshold: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface CustomerBranch {
  id: number;
  organizationAdminId: string | number;
  name: string;
  billingAddress: string;
  shippingAddress: string;
  gstNumber?: string;
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProductBranchPrice {
  id: number;
  organizationAdminId: string | number;
  branchId: number;
  productId: number;
  variationId?: string;
  price: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface Shipment {
  id: number;
  orderId: number;
  trackingNumber: string;
  courierPartner?: string;
  status: 'SHIPPED' | 'DELIVERED';
  shippedBy?: string | number | null;
  shippedAt: Date;
  deliveredAt?: Date;
  deliveredBy?: string | number | null;
}

export interface OrderEvent {
  id: number;
  orderId: number;
  eventType: string;
  actorId?: string | number | null;
  actorName?: string;
  fromStatus?: OrderStatus | string | null;
  toStatus?: OrderStatus | string | null;
  details?: string;
  createdAt: Date;
}

export type PaymentTransactionStatus =
  | 'INITIATED'
  | 'SUCCESS'
  | 'FAILED'
  | 'RECONCILED'
  | 'REFUND_PENDING'
  | 'REFUNDED'
  | 'REFUND_FAILED';

export interface PaymentTransaction {
  id: number;
  invoiceId: number;
  orderId: number;
  amount: number;
  provider: string;
  gatewayTransactionId?: string;
  status: PaymentTransactionStatus;
  payload?: any;
  reconciledAt?: Date;
  createdBy?: string | number | null;
  createdAt: Date;
  updatedAt?: Date;
}

export type NewProductData = Omit<Product, 'id'>;
export type NewOrderData = Omit<Order, 'id' | 'createdAt' | 'totalAmount'>;
export type CreateOrderData = NewOrderData & {
  submittedById?: number | string | null;
  customerAdminId?: number | string | null;
};
export type NewTestimonialData = Omit<Testimonial, 'id' | 'createdAt'>;
export type NewCustomerData = Omit<User, 'id' | 'role' | 'password'> & { password?: string };
export type NewInquiryData = Omit<Inquiry, 'id' | 'createdAt' | 'status'>;
export type NewCustomerBranchData = Omit<CustomerBranch, 'id' | 'createdAt' | 'updatedAt'>;

export type PublicPage = 'home' | 'products' | 'services' | 'how-it-works' | 'about' | 'contact' | 'who-we-serve' | 'why-agm';
